import { useEffect, useState } from "react";
import api from "@/lib/api";
import { eur, initials } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShiftsTab } from "@/pages/team/ShiftsTab";
import { AttendanceTab } from "@/pages/team/AttendanceTab";
import { AbsencesTab } from "@/pages/team/AbsencesTab";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const ROLES = ["admin", "manager", "reception", "stylist", "assistant"];
// Modello retribuzione allineato alla sezione Retribuzioni (motore paghe)
const COMP_TYPES = [
  { v: "none", l: "Nessuno" }, { v: "hourly", l: "Oraria" }, { v: "commission", l: "Percentuale" },
  { v: "hourly_commission", l: "Oraria + %" }, { v: "fixed", l: "Fisso" },
  { v: "fixed_commission", l: "Fisso + %" },
];
const FREQ = [{ v: "settimanale", l: "Settimanale" }, { v: "quindicinale", l: "Quindicinale" }, { v: "mensile", l: "Mensile" }];
const emptyComp = { type: "commission", hourly_rate: 0, fixed_salary: 0, service_pct: 30, product_pct: 10, revenue_target: 0, bonus: 0, bonus_note: "", frequency: "mensile", tiers: [] };
// Riepilogo compatto della retribuzione (compatibile col vecchio campo `amount`)
const compSummary = (c) => {
  if (!c || c.type === "none") return "—";
  const parts = [];
  const hr = c.hourly_rate || (c.type === "hourly" ? c.amount : 0);
  const fx = c.fixed_salary || (c.type === "fixed" ? c.amount : 0);
  if (fx) parts.push(`€${fx} fisso`);
  if (hr) parts.push(`€${hr}/h`);
  if (["commission", "hourly_commission", "fixed_commission"].includes(c.type) && c.service_pct) parts.push(`${c.service_pct}% serv.`);
  return parts.length ? parts.join(" · ") : "—";
};

export default function Team() {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const can = (p) => perms.includes("*") || perms.includes(p);
  const [team, setTeam] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = { name: "", email: "", role: "stylist", job_title: "", color: "#C9A24B", password: "", bookable_internal: true, bookable_online: true, enabled_service_ids: [], compensation: { ...emptyComp } };
  const [form, setForm] = useState(empty);
  const isAdmin = user?.role === "admin";

  const load = () => api.get("/team").then((r) => setTeam(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data || [])).catch(() => {}); }, []);

  const openEditor = (t) => { setEdit(t); setForm(t ? { ...empty, ...t, compensation: { ...emptyComp, ...(t.compensation || {}) }, enabled_service_ids: t.enabled_service_ids || [], password: "" } : { ...empty, compensation: { ...emptyComp } }); setOpen(true); };
  const save = async () => {
    if (!form.name) { toast.error("Nome obbligatorio"); return; }
    try {
      // Normalizza la retribuzione nel modello canonico del motore paghe
      const c = form.compensation || {};
      const compensation = {
        type: c.type || "commission",
        hourly_rate: Number(c.hourly_rate) || 0,
        fixed_salary: Number(c.fixed_salary) || 0,
        service_pct: Number(c.service_pct) || 0,
        product_pct: Number(c.product_pct) || 0,
        revenue_target: Number(c.revenue_target) || 0,
        bonus: Number(c.bonus) || 0,
        bonus_note: (c.bonus_note || "").trim(),
        frequency: c.frequency || "mensile",
        tiers: (c.tiers || []).filter((t) => t.over !== "" && t.over != null).map((t) => ({ over: Number(t.over) || 0, pct: Number(t.pct) || 0 })),
        excluded_service_ids: c.excluded_service_ids || [],
      };
      const body = { ...form, compensation }; if (!body.password) delete body.password;
      if (edit) await api.put(`/team/${edit.id}`, body); else await api.post("/team", body);
      toast.success("Salvato"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const toggleService = (id) => setForm((f) => {
    const cur = f.enabled_service_ids || [];
    return { ...f, enabled_service_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });

  const quickToggleOnline = async (t, v) => {
    setTeam((prev) => prev.map((m) => (m.id === t.id ? { ...m, bookable_online: v } : m)));
    try {
      await api.put(`/team/${t.id}`, { bookable_online: v });
      toast.success(v ? `${t.name} è ora prenotabile online` : `${t.name} non è più prenotabile online`);
    } catch (e) {
      setTeam((prev) => prev.map((m) => (m.id === t.id ? { ...m, bookable_online: !v } : m)));
      toast.error("Errore aggiornamento");
    }
  };

  const comp = form.compensation || {};
  const setComp = (k, v) => setForm((f) => ({ ...f, compensation: { ...f.compensation, [k]: v } }));

  const deleteMember = async (t) => {
    if (t.id === user?.id) { toast.error("Non puoi eliminare il tuo stesso profilo"); return; }
    if (!window.confirm(`Eliminare il collaboratore ${t.name}?\n\nLo storico vendite, incassi e provvigioni NON verranno eliminati e le statistiche del negozio resteranno invariate. Il collaboratore verrà solo rimosso dagli elenchi.`)) return;
    try {
      await api.delete(`/team/${t.id}`);
      toast.success("Collaboratore eliminato (fatturato e statistiche invariati)");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore durante l'eliminazione"); }
  };

  return (
    <div>
      <PageHeader title="Team" subtitle="Collaboratori, turni, presenze e assenze">
        {isAdmin && <Button onClick={() => openEditor(null)} data-testid="team-new-button"><Plus className="h-4 w-4 mr-1" /> Nuovo membro</Button>}
      </PageHeader>

      <Tabs defaultValue="membri">
        <TabsList className="mb-5">
          <TabsTrigger value="membri" data-testid="tab-membri">Membri</TabsTrigger>
          {can("shifts.read") && <TabsTrigger value="turni" data-testid="tab-turni">Turni</TabsTrigger>}
          {can("attendance.read") && <TabsTrigger value="presenze" data-testid="tab-presenze">Presenze</TabsTrigger>}
          {can("leave.read") && <TabsTrigger value="assenze" data-testid="tab-assenze">Ferie & Assenze</TabsTrigger>}
        </TabsList>

        <TabsContent value="membri">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {team.map((t) => (
              <Card key={t.id} className={`p-4 flex items-center gap-3 ${isAdmin ? "cursor-pointer hover:bg-accent/40" : ""}`} onClick={() => isAdmin && openEditor(t)} data-testid={`team-card-${t.id}`}>
                <Avatar className="h-12 w-12"><AvatarFallback style={{ background: (t.color||"#C9A24B")+"22", color: t.color }} className="font-semibold">{initials(t.name)}</AvatarFallback></Avatar>
                <div className="flex-1">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.job_title}</div>
                  <Badge variant="secondary" className="mt-1 text-[10px] capitalize">{t.role}</Badge>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {t.bookable_internal && <Badge variant="outline" className="text-[9px] px-1.5 py-0" data-testid={`badge-internal-${t.id}`}>Interno</Badge>}
                    {t.bookable_online && <Badge variant="outline" className="text-[9px] px-1.5 py-0" data-testid={`badge-online-${t.id}`}>Online</Badge>}
                  </div>
                </div>
                <div className="text-right text-xs text-muted-foreground flex flex-col items-end gap-2">
                  <span data-testid={`comp-summary-${t.id}`}>{compSummary(t.compensation)}</span>
                  {isAdmin && (
                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                      <span className="text-[10px] uppercase tracking-wide">Online</span>
                      <Switch checked={t.bookable_online === true} onCheckedChange={(v) => quickToggleOnline(t, v)} data-testid={`quick-online-${t.id}`} />
                    </div>
                  )}
                  {isAdmin && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={(e) => { e.stopPropagation(); deleteMember(t); }} data-testid={`team-delete-${t.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
          {!isAdmin && <p className="text-xs text-muted-foreground mt-4">Solo il Titolare/Admin puo modificare i membri del team.</p>}
        </TabsContent>

        {can("shifts.read") && <TabsContent value="turni"><ShiftsTab team={team} canWrite={can("shifts.write")} /></TabsContent>}
        {can("attendance.read") && <TabsContent value="presenze"><AttendanceTab team={team} canWrite={can("attendance.write")} /></TabsContent>}
        {can("leave.read") && <TabsContent value="assenze"><AbsencesTab team={team} canWrite={can("leave.write")} /></TabsContent>}
      </Tabs>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading text-2xl">{edit ? "Modifica membro" : "Nuovo membro"}</SheetTitle></SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="space-y-1.5"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-email" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Ruolo</Label><Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger data-testid="team-role"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Mansione</Label><Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Colore agenda</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-20 p-1" /></div>
            <div className="border-t border-border pt-4 space-y-3">
              <Label className="text-base">Prenotabilità in Agenda</Label>
              <p className="text-xs text-muted-foreground -mt-1">Indipendente dal ruolo: definisce se il membro può ricevere appuntamenti. Non modifica i permessi Admin.</p>
              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <div><div className="text-sm font-medium">Prenotabile internamente</div><div className="text-xs text-muted-foreground">Compare in Agenda e riceve appuntamenti dallo staff</div></div>
                <Switch checked={form.bookable_internal === true} onCheckedChange={(v) => setForm({ ...form, bookable_internal: v })} data-testid="team-bookable-internal" />
              </div>
              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <div><div className="text-sm font-medium">Prenotabile online</div><div className="text-xs text-muted-foreground">Selezionabile dalle clienti nel booking pubblico</div></div>
                <Switch checked={form.bookable_online === true} onCheckedChange={(v) => setForm({ ...form, bookable_online: v })} data-testid="team-bookable-online" />
              </div>
            </div>
            <div className="border-t border-border pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base">Servizi eseguibili</Label>
                <span className="text-xs text-muted-foreground" data-testid="team-services-count">
                  {(form.enabled_service_ids || []).length === 0 ? "Tutti i servizi" : `${(form.enabled_service_ids || []).length} selezionati`}
                </span>
              </div>
              <p className="text-xs text-muted-foreground -mt-1">Se non selezioni nulla, il membro può eseguire tutti i servizi. Il booking proporrà slot solo per i trattamenti abilitati.</p>
              {services.length > 0 && (
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setForm((f) => ({ ...f, enabled_service_ids: services.map((s) => s.id) }))} data-testid="team-services-all">Seleziona tutti</Button>
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setForm((f) => ({ ...f, enabled_service_ids: [] }))} data-testid="team-services-none">Nessuno (tutti)</Button>
                </div>
              )}
              <ScrollArea className="h-56 rounded-md border border-border p-2">
                {Object.entries(services.reduce((acc, s) => { const c = s.category || "Altro"; (acc[c] = acc[c] || []).push(s); return acc; }, {})).map(([cat, list]) => (
                  <div key={cat} className="mb-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">{cat}</div>
                    <div className="space-y-1.5">
                      {list.map((s) => (
                        <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer rounded px-1 py-0.5 hover:bg-accent/40" data-testid={`team-service-row-${s.id}`}>
                          <Checkbox checked={(form.enabled_service_ids || []).includes(s.id)} onCheckedChange={() => toggleService(s.id)} data-testid={`team-service-${s.id}`} />
                          <span className="flex-1">{s.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                {services.length === 0 && <p className="text-xs text-muted-foreground p-2">Nessun servizio disponibile.</p>}
              </ScrollArea>
            </div>
            <div className="space-y-1.5"><Label>Password {edit ? "(lascia vuoto per non cambiare)" : "(vuoto = password provvisoria assegnata dal sistema)"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={edit ? "" : "Imposta una password"} /></div>
            <div className="border-t border-border pt-4 space-y-3">
              <Label className="text-base">Retribuzione</Label>
              <p className="text-[11px] text-muted-foreground -mt-1">I componenti sono cumulabili: fisso + oraria + commissioni + bonus si sommano. Le fasce progressive avanzate si gestiscono nella sezione Retribuzioni.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Tipo</Label>
                  <Select value={comp.type} onValueChange={(v) => setComp("type", v)}><SelectTrigger data-testid="team-comp-type"><SelectValue /></SelectTrigger><SelectContent>{COMP_TYPES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Frequenza</Label>
                  <Select value={comp.frequency} onValueChange={(v) => setComp("frequency", v)}><SelectTrigger data-testid="team-comp-frequency"><SelectValue /></SelectTrigger><SelectContent>{FREQ.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              {comp.type !== "none" && (
                <div className="grid grid-cols-2 gap-3">
                  {(comp.type === "hourly" || comp.type === "hourly_commission") && <div className="space-y-1.5"><Label className="text-xs">Paga oraria (€)</Label><Input type="number" value={comp.hourly_rate || 0} onChange={(e) => setComp("hourly_rate", +e.target.value)} data-testid="team-comp-hourly" /></div>}
                  {(comp.type === "fixed" || comp.type === "fixed_commission") && <div className="space-y-1.5"><Label className="text-xs">Fisso periodo (€)</Label><Input type="number" value={comp.fixed_salary || 0} onChange={(e) => setComp("fixed_salary", +e.target.value)} data-testid="team-comp-fixed" /></div>}
                  {(comp.type === "commission" || comp.type === "hourly_commission" || comp.type === "fixed_commission") && <>
                    <div className="space-y-1.5"><Label className="text-xs">% Servizi</Label><Input type="number" value={comp.service_pct || 0} onChange={(e) => setComp("service_pct", +e.target.value)} data-testid="team-comp-service-pct" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">% Prodotti</Label><Input type="number" value={comp.product_pct || 0} onChange={(e) => setComp("product_pct", +e.target.value)} data-testid="team-comp-product-pct" /></div>
                  </>}
                </div>
              )}
              {(comp.type === "commission" || comp.type === "hourly_commission" || comp.type === "fixed_commission") && (
                <div className="space-y-1.5"><Label className="text-xs">Obiettivo minimo fatturato (€)</Label><Input type="number" value={comp.revenue_target || 0} onChange={(e) => setComp("revenue_target", +e.target.value)} data-testid="team-comp-target" /><p className="text-[11px] text-muted-foreground">La % Servizi si applica solo sulla parte oltre l'obiettivo.</p></div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Bonus (€)</Label><Input type="number" value={comp.bonus || 0} onChange={(e) => setComp("bonus", +e.target.value)} data-testid="team-comp-bonus" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Nota bonus</Label><Input value={comp.bonus_note || ""} onChange={(e) => setComp("bonus_note", e.target.value)} data-testid="team-comp-bonus-note" /></div>
              </div>
            </div>
            <Button className="w-full" onClick={save} data-testid="team-save">Salva</Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
